import React, { ChangeEvent, useMemo } from 'react';
import {
  Box,
  Card,
  Link,
  Radio,
  Tag,
  Text,
  Title,
} from '@nimbus-ds/components';
import { ExclamationCircleIcon } from '@nimbus-ds/icons';
import { FormField } from '@nimbus-ds/patterns';
import { Trans, useTranslation } from 'react-i18next';
import { OrderDetailsResponseDto } from '@tiendanube/common';
import { CurrencyPrice, Stack } from 'commons/components';
import { InterfaceNameBooleanValue, InterfaceNameValue } from 'commons/types';
import { PAYMENTS_PARTIAL_REFUND_TAG } from '../../../../../../../App/featuresFlags';
import { useHasTags } from '../../../../../../../App/hooks';
import { getSymbolFromCurrency } from '../../../../../../Auth/hooks/useGetCurrency/useGetCurrency';
import useTranslationOrders from '../../../../../useTranslationOrders';
import { useCanBeRefunded } from '../../../hooks';
import { useNuvemPagoRedirect } from '../../../hooks/useNuvemPagoRedirect';
import {
  RefundAttributes,
  RefundAttributesTypes,
} from '../../../hooks/useRefundOrder/useRefundOrder';

interface Attributes {
  order: OrderDetailsResponseDto;
  onChange: (data: InterfaceNameValue | InterfaceNameBooleanValue) => void;
  values: RefundAttributes;
}

function RefundAmountSelectorCard({
  order,
  values,
  onChange,
}: Attributes): React.JSX.Element {
  const {
    i18n: { language },
  } = useTranslation();
  const t = useTranslationOrders(
    `refund.setUpRefundDetails.amountSelectorCard`,
  );
  const setType = (type: string) => {
    onChange({ name: 'type', value: type });
  };

  const setValue = (value: string) => {
    onChange({ name: 'value', value: value });
  };

  const gateway = order.payment?.gateway ?? '';
  const nuvemPagoRedirect = useNuvemPagoRedirect(gateway);
  const { canBePartiallyRefunded } = useCanBeRefunded(order);

  const isPartialRefundEnabled =
    canBePartiallyRefunded() && order.payment.supportsPartialRefund;
  const [hasPartialRefundTag] = useHasTags([PAYMENTS_PARTIAL_REFUND_TAG]);

  const refundableAmount = order.payment.refundableAmount?.value ?? '0.00';

  const setToTotalRefund = () => {
    setType(RefundAttributesTypes.TOTAL);
    setValue(refundableAmount);
  };
  const setToPartialRefund = () => {
    setType(RefundAttributesTypes.PARTIAL_BY_VALUE);
    setValue('');
  };

  const updateAmount = (value: string) => {
    if (Number(value) === Number(refundableAmount)) {
      setType(RefundAttributesTypes.TOTAL);
    } else {
      if (values.type === RefundAttributesTypes.TOTAL) {
        onChange({ name: 'returnsStock', value: false });
      }
      setType(RefundAttributesTypes.PARTIAL_BY_VALUE);
    }
    setValue(value);
  };

  const onChangeAmountInput = (event: ChangeEvent<HTMLInputElement>) =>
    updateAmount(event.target.value);

  const hasZeroValueError = useMemo(
    () => values.value !== '' && Number(values.value) <= 0,
    [values],
  );
  const hasHighValueError = useMemo(
    () => Number(values.value) > Number(refundableAmount),
    [refundableAmount, values],
  );
  const hasError = useMemo(
    () => hasZeroValueError || hasHighValueError,
    [hasHighValueError, hasZeroValueError],
  );

  const inputErrorText = useMemo(() => {
    if (hasZeroValueError) {
      return t('errors.mustBeGreaterThanZero');
    }
    if (hasHighValueError) {
      return t('errors.mustBeLowerThanRefundableAmount');
    }
    return undefined;
  }, [hasHighValueError, hasZeroValueError, t]);

  const formatAmount = (amount: string): string =>
    Intl.NumberFormat(language, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount));

  if (hasPartialRefundTag) {
    return (
      <Card>
        <Title as="h3">{t('title')}</Title>

        <Box display="flex" gap="4" justifyContent="space-between">
          <FormField.Input
            disabled={!isPartialRefundEnabled}
            type="number"
            min="0"
            max={refundableAmount}
            value={isPartialRefundEnabled ? values.value : refundableAmount}
            placeholder={formatAmount('0')}
            onChange={onChangeAmountInput}
            append={
              <Text color="neutral-textLow">
                {getSymbolFromCurrency(order.currency)}
              </Text>
            }
            appendPosition="start"
            appearance={hasError ? 'danger' : 'none'}
            helpText={inputErrorText}
            showHelpText={hasError}
            helpIcon={hasError ? ExclamationCircleIcon : undefined}
            autoFocus
          />
        </Box>

        <Box
          id="divider"
          backgroundColor="neutral-surfaceHighlight"
          height="1px"
        />

        <Text color="neutral-textLow">
          {`${t('description')} ${formatAmount(refundableAmount)}`}
        </Text>
      </Card>
    );
  }

  return (
    <Card>
      <Box display="flex" gap="4" justifyContent="space-between">
        <Stack align="stretch" justify="flex-start" spacing="tight">
          <Radio
            id="total"
            onChange={setToTotalRefund}
            name="total"
            checked={values.type === 'total'}
          />
          <Stack column gap="1">
            <Stack alignSelf="flex-start">
              <Text color="primary-textHigh">{t('old.total.title')}</Text>
            </Stack>
            <Stack alignSelf="flex-start">
              <Text color="neutral-textLow">{t('old.total.description')}</Text>
            </Stack>
          </Stack>
        </Stack>
        <Box justifyContent="flex-start">
          <CurrencyPrice
            price={Number(refundableAmount)}
            currency={order.currency}
            block
            textAlign="right"
          />
        </Box>
      </Box>

      <Box
        id="divider"
        backgroundColor="neutral-surfaceHighlight"
        height="1px"
      />

      <Box display="flex" gap="4" justifyContent="space-between">
        <Stack spacing="tight" justify="flex-start" align="flex-start">
          <Radio
            disabled={!isPartialRefundEnabled}
            onChange={setToPartialRefund}
            name="partial_by_value"
            checked={values.type === 'partialByValue'}
          />
          <Text color="primary-textHigh">{t('old.partialByValue.title')}</Text>
          {!isPartialRefundEnabled && (
            <Box minWidth="fit-content">
              <Tag appearance="primary">
                <Text lineClamp={1}>{t('old.tagText')}</Text>
              </Tag>
            </Box>
          )}
        </Stack>
        <Box justifyContent="flex-start" flex="0 0 30%">
          <FormField.Input
            disabled={values.type !== RefundAttributesTypes.PARTIAL_BY_VALUE}
            type="number"
            min="0"
            max={refundableAmount}
            value={values.type === 'partialByValue' ? values.value : ''}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setValue(event.target.value)
            }
            append={
              <Text color="neutral-textLow">
                {getSymbolFromCurrency(order.currency)}
              </Text>
            }
            appendPosition="start"
            appearance="none"
          />
        </Box>
      </Box>
      {!isPartialRefundEnabled && nuvemPagoRedirect.isNuvemPagoPayment() && (
        <Text color="neutral-textLow">
          <Trans
            t={t}
            i18nKey="old.partialByValue.nuvemPagoLink"
            components={{
              ExternalLink: (
                <Link
                  as="a"
                  appearance="primary"
                  textDecoration="none"
                  href={nuvemPagoRedirect.partialRefundURL(order.id)}
                  target="_blank"
                >
                  {' '}
                </Link>
              ),
            }}
          />
        </Text>
      )}
    </Card>
  );
}

export default RefundAmountSelectorCard;
